import React from 'react';
import { useEventCallback, usePrevious } from '../../hooks';
import { inDOM } from '../../utils';
import { StickyProps } from './Sticky';

type StickyState = 'fixed' | 'bound' | null;

export function useSticky(props: Omit<StickyProps, 'children'>) {
    const isClient = inDOM();

    const {
        anchor = 'top',
        disabled = false,
        context = isClient ? document.body : null,
        scrollContext = isClient ? window : null,
        push = false,
        offset = 0,
        bottomOffset = 0,
        fullWidth = false,
        onStick,
        onUnstick,
    } = props;

    const prevDisabled = usePrevious<boolean>(disabled);
    const prevScrollContext = usePrevious<Element | Window | null>(scrollContext);

    const [sticky, setSticky] = React.useState<StickyState>(null);

    const [bottom, setBottom] = React.useState<number | undefined>(undefined);
    const [top, setTop] = React.useState<number | undefined>(undefined);

    const [pushing, setPushing] = React.useState<boolean>(false);

    const triggerRef = React.useRef<HTMLDivElement | null>(null);
    const stickyRef = React.useRef<HTMLDivElement | null>(null);

    // DOM Rectangle references
    const contextRect = React.useRef<DOMRect | null>(null);
    const triggerRect = React.useRef<DOMRect | null>(null);
    const stickyRect = React.useRef<DOMRect | null>(null);

    // Event handling refs
    const ticking = React.useRef<boolean>(false);
    const frameId = React.useRef<number | null>(null);

    const togglePushing = React.useCallback(
        (value: boolean) => {
            if (push) {
                setPushing(value);
            }
        },
        [push]
    );

    const assignRects = React.useCallback(() => {
        triggerRect.current = triggerRef.current?.getBoundingClientRect() || null;
        contextRect.current = context?.getBoundingClientRect() || null;
        stickyRect.current = stickyRef.current?.getBoundingClientRect() || null;
    }, [context]);

    // Has the trigger element touched the top of the screen?
    const didReachOriginalPosition = React.useCallback(() => {
        if (!triggerRect.current || !stickyRect.current) {
            return false;
        }
        if (anchor === 'bottom') {
            return triggerRect.current.top + stickyRect.current.height + bottomOffset > window.innerHeight;
        }
        return triggerRect.current.top < offset;
    }, [anchor, bottomOffset, offset]);

    // Match the width of the sticky element to the trigger element.
    const checkStickyWidth = React.useCallback(() => {
        if (!sticky || !stickyRef.current || !triggerRect.current) {
            return;
        }
        stickyRef.current.style.width = `${triggerRect.current.width}px`;
    }, [sticky]);

    // Has the sticky element touched the bottom of the screen?
    const didTouchScreenBottom = React.useCallback(() => {
        if (!contextRect.current) {
            return false;
        }
        return contextRect.current.bottom + bottomOffset > window.innerHeight;
    }, [bottomOffset]);

    // Has the sticky element reached the bottom of the context element?
    const didReachContextBottom = React.useCallback(() => {
        if (!stickyRect.current || !contextRect.current) {
            return false;
        }
        return stickyRect.current.height + offset >= contextRect.current.bottom;
    }, [offset]);

    //  Has the sticky element reached the top of the context element?
    const didReachContextTop = (): boolean => {
        if (!stickyRect.current || !contextRect.current) {
            return false;
        }
        return window.innerHeight - stickyRect.current.height <= contextRect.current.top;
    };

    // Has the sticky element reached the starting point?
    const didReachStartingPoint = React.useCallback(() => {
        if (!stickyRect.current || !triggerRect.current) {
            return false;
        }
        return stickyRect.current.top <= triggerRect.current.top;
    }, []);

    // Is the sticky element taller than the window?
    const isOversized = React.useCallback(() => {
        if (!stickyRect.current) {
            return false;
        }
        return stickyRect.current.height > window.innerHeight;
    }, []);

    const bindToContextBottom = React.useCallback(() => {
        if (sticky === 'bound') {
            return;
        }

        setSticky('bound');
        togglePushing(true);
    }, [sticky, togglePushing]);

    const fixToScreenTop = React.useCallback(() => {
        if (sticky === 'fixed') {
            return;
        }

        setSticky('fixed');
        setBottom(undefined);
        setTop(offset);

        onStick?.();
    }, [offset, onStick, sticky]);

    const fixToScreenBottom = React.useCallback(() => {
        if (sticky === 'fixed') {
            return;
        }

        setSticky('fixed');
        setBottom(bottomOffset);
        setTop(undefined);

        onStick?.();
    }, [bottomOffset, onStick, sticky]);

    const release = React.useCallback(() => {
        if (sticky === null) {
            return;
        }

        setSticky(null);
        togglePushing(false);

        onUnstick?.();
    }, [onUnstick, sticky, togglePushing]);

    const update = React.useCallback((): void => {
        ticking.current = false;
        assignRects();

        if (pushing) {
            if (didReachStartingPoint()) {
                release();
                return;
            }

            if (didTouchScreenBottom()) {
                fixToScreenBottom();
                return;
            }

            bindToContextBottom();
            return;
        }

        if (isOversized()) {
            if (!contextRect.current) {
                return;
            }

            if (contextRect.current.top > 0) {
                release();
                return;
            }

            if (contextRect.current.bottom < window.innerHeight) {
                bindToContextBottom();
                return;
            }
        }

        if (anchor === 'bottom') {
            if (didReachOriginalPosition()) {
                if (didReachContextTop()) {
                    release();
                    return;
                }
                fixToScreenBottom();
                return;
            }
        }

        // Default case (not pushing, not oversized, anchor === 'top')
        if (didReachOriginalPosition()) {
            if (didReachContextBottom()) {
                bindToContextBottom();
                return;
            }

            fixToScreenTop();
            return;
        }

        release();
    }, [
        anchor,
        assignRects,
        bindToContextBottom,
        didReachContextBottom,
        didReachOriginalPosition,
        didReachStartingPoint,
        didTouchScreenBottom,
        fixToScreenBottom,
        fixToScreenTop,
        isOversized,
        pushing,
        release,
    ]);

    const handleUpdate = useEventCallback((event: Event | null): void => {
        // Throttle updates using requestAnimationFrame
        if (!ticking.current) {
            ticking.current = true;
            frameId.current = requestAnimationFrame(() => {
                update();
                if (event?.type === 'resize' && !fullWidth) {
                    checkStickyWidth();
                }
            });
        }
    });

    const addListeners = React.useCallback(
        (element: Element | Window | null): void => {
            if (element) {
                element.addEventListener('scroll', handleUpdate);
                element.addEventListener('resize', handleUpdate);
            }
        },
        [handleUpdate]
    );

    const removeListeners = React.useCallback(
        (element: Element | Window | null): void => {
            if (element) {
                element.removeEventListener('scroll', handleUpdate);
                element.removeEventListener('resize', handleUpdate);
            }
        },
        [handleUpdate]
    );

    // onmount
    React.useEffect(() => {
        if (!isClient) {
            return undefined;
        }

        if (!disabled) {
            handleUpdate(null);
            removeListeners(scrollContext);
            addListeners(scrollContext);
        }

        return () => {
            if (!disabled) {
                removeListeners(scrollContext);
            }
            if (frameId.current) {
                cancelAnimationFrame(frameId.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (disabled && !prevDisabled) {
            // Sticky was disabled
            removeListeners(scrollContext);
            setSticky(null);
        }
        if (!disabled && prevDisabled) {
            // Sticky was enabled
            handleUpdate(null);
            addListeners(scrollContext);
        }
    }, [addListeners, disabled, handleUpdate, prevDisabled, removeListeners, scrollContext]);

    React.useEffect(() => {
        if (scrollContext !== prevScrollContext && prevScrollContext !== undefined) {
            // The scroll context has changed
            removeListeners(prevScrollContext);
            addListeners(scrollContext);
        }
    }, [scrollContext, prevScrollContext, removeListeners, addListeners]);

    // Prepare dynamic styles
    const stickyStyles: React.CSSProperties = React.useMemo(() => {
        if (sticky == null) {
            return {};
        }
        const fixed = sticky === 'fixed';
        const bound = sticky === 'bound';
        return {
            bottom: bound ? 0 : bottom,
            top: fixed ? top : undefined,
            width: fullWidth ? '100%' : triggerRect.current?.width || 0,
            left: fullWidth ? 0 : undefined,
        };
    }, [bottom, fullWidth, sticky, top]);

    // The trigger matches the height of the sticky element while it is stuck, preventing any layout shift.
    const triggerStyles: React.CSSProperties = React.useMemo(() => {
        return sticky !== null ? { height: stickyRect.current?.height } : {};
    }, [sticky]);

    return React.useMemo(
        () => ({
            sticky,
            triggerRef,
            stickyRef,
            stickyStyles,
            triggerStyles,
        }),
        [sticky, stickyStyles, triggerStyles]
    );
}
